import React, { useMemo, useState } from "react";

import { useAuth } from "@/context/AuthContext";
import brand from "@/config/brand";
import { gameApi } from "@/lib/api";
import { formatCoins, formatCoinsLabel } from "@/lib/coins";

import { StyledCoinCenterContent } from "./styles";

const ACCOUNT_OPTION = "Account";
const HOW_IT_WORKS_OPTION = "How It Works";

const getSafeNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const CoinCenterContent = ({ option }) => {
  const { user, updateBalance, refreshProfile } = useAuth();
  const [isClaiming, setIsClaiming] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const isAuthenticated = Boolean(user?.profile);
  const profile = user?.profile;
  const balance = getSafeNumber(user?.balance ?? profile?.balance);
  const refillReady = balance < 10;

  const accountStats = useMemo(
    () => [
      {
        label: "Level",
        value: getSafeNumber(profile?.level).toString(),
        description: "Your saved account level.",
      },
      {
        label: "XP To Next",
        value: formatCoins(getSafeNumber(profile?.xp_to_next)),
        description: "Progress needed for the next level.",
      },
      {
        label: "Total Played",
        value: formatCoins(getSafeNumber(profile?.total_wagered)),
        description: "Lifetime coins wagered across your account.",
      },
      {
        label: "Total Won",
        value: formatCoins(getSafeNumber(profile?.total_won)),
        description: "Lifetime coins returned to your balance.",
      },
    ],
    [profile]
  );

  const handleClaimCoins = async () => {
    setNotice("");
    setError("");
    setIsClaiming(true);

    try {
      const response = await gameApi.user.claimRefill();
      if (!response.success || !response.data) {
        throw new Error(response.error || "Unable to claim coins right now.");
      }

      updateBalance(response.data.balance);
      await refreshProfile();
      setNotice(`1,000 free coins added. New balance: ${formatCoinsLabel(response.data.balance)}.`);
    } catch (claimError) {
      setError(claimError.message || "Unable to claim coins right now.");
    } finally {
      setIsClaiming(false);
    }
  };

  if (option === ACCOUNT_OPTION) {
    return (
      <StyledCoinCenterContent>
        <div className="panel">
          <div className="eyebrow">Account Overview</div>
          <h1 className="title">Saved Progress</h1>
          <p className="copy">
            {isAuthenticated
              ? "Your account keeps your free-play balance, level, and lifetime stats across sessions."
              : "Create an account to keep one saved free-play balance and track your progress across sessions."}
          </p>
        </div>

        <div className="grid">
          {accountStats.map((stat) => (
            <div className="card" key={stat.label}>
              <div className="label">{stat.label}</div>
              <div className="value">{stat.value}</div>
              <p className="copy small-copy">{stat.description}</p>
            </div>
          ))}
        </div>
      </StyledCoinCenterContent>
    );
  }

  if (option === HOW_IT_WORKS_OPTION) {
    return (
      <StyledCoinCenterContent>
        <div className="panel">
          <div className="eyebrow">Social Casino</div>
          <h1 className="title">How Free Play Works</h1>
          <p className="copy">
            {brand.casinoName} is a free-to-play social casino. Coins are for entertainment only and do not have cash value.
          </p>
        </div>

        <div className="grid">
          <div className="card">
            <div className="label">No Deposits</div>
            <p className="copy small-copy">
              There are no deposits, withdrawals, or crypto purchases in this flow. Play uses free coins only.
            </p>
          </div>
          <div className="card">
            <div className="label">Saved Accounts</div>
            <p className="copy small-copy">
              Create an account to keep your balance, level, and history. Guest Free Play is still available from {brand.originalsLabel}.
            </p>
          </div>
          <div className="card">
            <div className="label">Refill Rule</div>
            <p className="copy small-copy">
              When your saved balance drops below 10 coins, the Coin Center lets you refill back to 1,000 coins.
            </p>
          </div>
          <div className="card">
            <div className="label">Playable Games</div>
            <p className="copy small-copy">
              Gold Reels, Mines, and Crash can all be launched in Free Play. Sign in when you want to keep progress.
            </p>
          </div>
        </div>
      </StyledCoinCenterContent>
    );
  }

  return (
    <StyledCoinCenterContent>
      <div className="hero">
        <div>
          <div className="eyebrow">Coin Center</div>
          <h1 className="title">Free-Play Balance</h1>
          <p className="copy">
            {isAuthenticated
              ? "Your saved account balance is used across the social casino."
              : "Free Play is available instantly. Sign in to save your balance and claim account refills."}
          </p>
        </div>
        <div className="hero-balance">
          <div className="label">Current Balance</div>
          <div className="amount">{formatCoins(balance)}</div>
          <div className="subtext">Coins</div>
        </div>
      </div>

      <div className="grid">
        <div className="card">
          <div className="label">Refill Amount</div>
          <div className="value">1,000 Coins</div>
          <p className="copy small-copy">Available for saved accounts once balance drops below 10 coins.</p>
        </div>
        <div className="card">
          <div className="label">Current Status</div>
          <div className="value">{isAuthenticated ? "Saved Account" : "Guest Free Play"}</div>
          <p className="copy small-copy">
            {isAuthenticated
              ? "Your coin balance and level are attached to your account."
              : "Guest sessions can launch games instantly, but account progress is not saved here."}
          </p>
        </div>
      </div>

      <div className="panel">
        <div className="action-row">
          <div>
            <div className="label">Claim Free Coins</div>
            <p className="copy small-copy">
              {isAuthenticated
                ? refillReady
                  ? "Your balance is low enough to claim a refill."
                  : "Spend down below 10 coins to unlock the next refill."
                : "Sign in to save your balance and use account refills."}
            </p>
          </div>
          <button
            className="primary-button"
            type="button"
            onClick={handleClaimCoins}
            disabled={!isAuthenticated || !refillReady || isClaiming}
          >
            {isClaiming ? "Claiming..." : "Claim 1,000 Coins"}
          </button>
        </div>

        {notice ? <div className="status success">{notice}</div> : null}
        {error ? <div className="status error">{error}</div> : null}
      </div>
    </StyledCoinCenterContent>
  );
};

export default CoinCenterContent;
